// MODEL-KEYS-PLANS: Connections › API keys › Model access (owners and admins, company scope).
// "Ours (billed)": our keys, token use billed through the wallet. "Our own keys": the company's
// provider keys below (one per provider), paid to the provider; we bill no tokens. Shown only when
// the plan allows own keys (or the company is already on them, so it can switch back). A provider
// the gateway's models need with no company key shows as missing.
import { Checkbox, InfoTip, Segmented, toast } from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Api } from "../../api/client";
import { problemText } from "../settings/problem-text";
import { providerLabel } from "../subscriptions/copy";
import { subscriptionKeys } from "../subscriptions/queries";

const accessKey = (co: string) => [...subscriptionKeys.all(co), "model-access"] as const;

export const modelAccessQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: accessKey(co),
    queryFn: () => api.client.call("modelAccess.get", { params: { co } }),
  });

export function ModelAccess({ api, co }: { api: Api; co: string }) {
  const qc = useQueryClient();
  const q = useQuery(modelAccessQuery(api, co));
  const save = useMutation({
    mutationFn: (body: { mode: "ours" | "own"; fallback_to_ours: boolean }) =>
      api.client.call("modelAccess.put", { params: { co }, body }),
    onSuccess: (v) => qc.setQueryData(accessKey(co), v),
    onError: (e) => toast.add({ title: problemText(e, "Not saved."), type: "error" }),
  });
  const v = q.data;
  if (!v || (!v.allowed && v.mode !== "own")) return null;
  const held = new Set(v.providers.map((p) => p.provider));
  const missing = v.needed.filter((p) => !held.has(p));
  return (
    <section
      aria-label="Model access"
      className="flex flex-col gap-2 rounded-md border border-subtle px-3 py-2.5"
      data-slot="model-access"
      data-mode={v.effective}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="flex items-center gap-1 text-sm font-medium">
          Model access
          <InfoTip label="About model access">
            Ours: we provide the AI model keys and bill token use from your wallet. Our own keys:
            your AI models run on your provider keys below (one per provider); you pay the provider
            and we bill no tokens.
          </InfoTip>
        </h2>
        <span className="ml-auto">
          <Segmented
            size="sm"
            aria-label="Model access"
            value={v.mode}
            onValueChange={(mode) =>
              save.mutate({ mode: mode as "ours" | "own", fallback_to_ours: v.fallback_to_ours })
            }
            options={[
              { value: "ours", label: "Ours (billed)" },
              { value: "own", label: "Our own keys" },
            ]}
          />
        </span>
      </div>
      {v.mode === "own" ? (
        <>
          <div className="flex items-center gap-2 text-[13px]">
            <Checkbox
              checked={v.fallback_to_ours}
              disabled={save.isPending}
              aria-label="Fall back to ours (billed)"
              onCheckedChange={(c) => save.mutate({ mode: "own", fallback_to_ours: c === true })}
            />
            Fall back to ours (billed)
            <InfoTip label="About falling back">
              When a model's provider has no key here, run it on our keys and bill it. Off: that
              call fails with a message.
            </InfoTip>
          </div>
          {!v.allowed ? (
            <p className="text-[13px] text-muted-foreground">
              Your plan no longer includes own keys: calls use ours (billed).
            </p>
          ) : missing.length ? (
            <p className="text-[13px] text-muted-foreground" data-slot="model-access-missing">
              No key yet: {missing.map(providerLabel).join(", ")}
            </p>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
